import { caughtReportBody, isAbortError, isShownRefusal, refusalReportBody } from '@bms2/shared';
import { apiFetch, lastFailedRequest } from '../pages/records/api';

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). The one route a browser fault is reported through (decision 0822). */
export const SYSTEM_ERRORS_BROWSER_PATH = '/system-errors/browser';

/** 🔨 A page that faults in a loop reports a bounded number of times; the server's per-minute limit folds the rest into a count. */
export const BROWSER_REPORTS_PER_PAGE = 10;

let reported = 0;

/** Test seam: a fresh page load, as far as the cap is concerned. */
export function resetBrowserReports(): void {
  reported = 0;
}

/**
 * 🔒 A browser fault's row (piece 9; decision 0822; T271): the error's kind,
 * message and stack, the page's path, the request id of the last failed read
 * and the bundle's commit. The server sanitises all of it before it is stored.
 * 🔨 The report never throws: a report that fails is dropped, because a failing
 * report reported again is the loop this listener would otherwise become.
 */
export async function reportBrowserError(error: unknown, apiBaseUrl = '', extraStack: string | null = null, where: string | null = null): Promise<string | null> {
  if (isAbortError(error) || reported >= BROWSER_REPORTS_PER_PAGE) return null;
  reported += 1;
  const body = {
    ...caughtReportBody({ where, error, extraStack, page: typeof window === 'undefined' ? '/' : window.location.pathname, appVersion: typeof __COMMIT__ === 'string' ? __COMMIT__ : 'unknown' }),
    requestId: lastFailedRequest(),
    // 🔒 The page's own origin, so the server can tell a frame of ours from an injected script's (T363; decision 0998).
    pageOrigin: typeof window === 'undefined' ? null : window.location.origin,
  };
  try {
    const response = await apiFetch(`${apiBaseUrl}${SYSTEM_ERRORS_BROWSER_PATH}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
    if (!response.ok || typeof response.json !== 'function') return null;
    const answer = (await response.json().catch(/* silent: the report's own body read; a failing report reported again is the loop the report refuses. */ () => null)) as { reference?: unknown } | null;
    return typeof answer?.reference === 'string' ? answer.reference : null;
  } catch {
    // silent: the report's own write; a failing report reported again is the loop the report refuses.
    return null;
  }
}

let caughtBase: string | null = null;

/**
 * 🔒 The signed-in frame turns caught reports on with its API base (decision 2200). 🔨 Nothing reports before it
 * does, so a page open without a session posts no report, as piece 9 has it, and a test that draws a page alone
 * sends no request it did not stub.
 */
export function registerCaughtReports(apiBaseUrl: string | null): void {
  caughtBase = apiBaseUrl;
}

/**
 * 🔨 A request the server never answered posts nothing (decision 2203): its report would cross the same broken
 * line. A request the page cancelled is already left out by the report itself. The browser's words for a fetch
 * that reached nobody differ, so each is named.
 */
export function unanswered(error: unknown): boolean {
  return error instanceof TypeError && /^(Failed to fetch|NetworkError when attempting to fetch resource\.|Load failed|Network request failed)$/.test(error.message);
}

/**
 * 🔒 A browser catch that swallows a failed read or write, or shows the person a sentence, posts its row through
 * the report route (piece 9; decision 2200; T1461). Each catch names where it stands, so the row says which wrote
 * it. A frame from here rides beside the error's own, so a plain value with no stack still reads as the
 * application's own fault. 🔨 It never throws and never waits.
 */
export function reportCaughtInBrowser(where: string, error: unknown): void {
  if (caughtBase === null || unanswered(error)) return;
  void reportBrowserError(error, caughtBase, new Error(`caught at ${where}`).stack ?? null, where);
}

/** 🔨 A page that meets refusals in a loop reports a bounded number of them; the server's per-minute limit folds the rest. Counted apart from faults, so refusals never crowd a fault out. */
export const REFUSAL_REPORTS_PER_PAGE = 10;

let refusalsReported = 0;

/** Test seam: a fresh page load, as far as the refusal cap is concerned. */
export function resetRefusalReports(): void {
  refusalsReported = 0;
}

/**
 * 🔒 A refusal the browser hands a screen to show posts its row through the report route, source Refusal (piece 9;
 * decision 11940): the status as the kind, the sentence as the message, the page's path and the answer's request id,
 * so the server's own Refusal row and this one read as one refusal seen from both ends. 🔨 Nothing reports before the
 * signed-in frame turns reports on, a 401 is the session ending and posts nothing, and it never throws or waits.
 */
export function reportRefusalInBrowser(status: number, sentence: string | null, requestId: string | null): void {
  if (caughtBase === null || !isShownRefusal(status) || refusalsReported >= REFUSAL_REPORTS_PER_PAGE) return;
  refusalsReported += 1;
  const body = {
    ...refusalReportBody({ status, sentence, page: typeof window === 'undefined' ? '/' : window.location.pathname, appVersion: typeof __COMMIT__ === 'string' ? __COMMIT__ : 'unknown' }),
    requestId,
  };
  void apiFetch(`${caughtBase}${SYSTEM_ERRORS_BROWSER_PATH}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) })
    .catch(/* silent: the refusal's own report; a failing report reported again is the loop the report refuses. */ () => undefined);
}
